'use client';
import { Box } from '@mui/material';

import { EmptyState, ErrorState, HomeMarketCardSkeleton, LoadMoreButton } from '@/components';
import { useT } from '@/hooks';
import type { EventResponse } from '@/types';
import { MarketListByTab } from './MarketListByTab';

interface HomeContentProps {
  isLoading: boolean;
  error: unknown;
  events: EventResponse[];
  activeCategorySlug: string;
  selectedFilter?: string;
  pagination: {
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
  };
  onTradeClick: (marketId: string, outcomeId: string, isBuyOrder: boolean) => void;
  onFetchNextPage: () => void;
  onRetry: () => void;
  isResolvedFilter?: boolean;
  gridCols?: {
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
  };
  skeletonGridStyles?: {
    xs?: string;
    sm?: string;
    md?: string;
    lg?: string;
  };
}

export const HomeContent = ({
  isLoading,
  error,
  events,
  activeCategorySlug,
  selectedFilter,
  pagination,
  onTradeClick,
  onFetchNextPage,
  onRetry,
  isResolvedFilter,
  gridCols = { xs: 1, sm: 2, md: 3, lg: 4 },
  skeletonGridStyles = {
    xs: '1fr',
    sm: 'repeat(2, minmax(0, 1fr))',
    md: 'repeat(4, minmax(0, 1fr))',
    lg: 'repeat(4, minmax(0, 1fr))',
  },
}: HomeContentProps) => {
  const { t } = useT();

  if (isLoading) {
    return <HomeMarketCardSkeleton gridStyles={skeletonGridStyles} />;
  }

  if (error) {
    return (
      <ErrorState title="Failed to load markets" message="Please try again." onRetry={onRetry} />
    );
  }

  if (!events || events.length === 0) {
    return (
      <EmptyState
        title={t('sports.noMarketsAvailable', { defaultValue: 'No markets available' })}
      />
    );
  }

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: `repeat(${gridCols.xs}, minmax(0, 1fr))`,
            sm: `repeat(${gridCols.sm}, minmax(0, 1fr))`,
            md: `repeat(${gridCols.md}, minmax(0, 1fr))`,
            lg: `repeat(${gridCols.lg}, minmax(0, 1fr))`,
          },
          gap: 1.5,
          width: '100%',
        }}
      >
        <MarketListByTab
          activeCategorySlug={activeCategorySlug}
          selectedFilter={selectedFilter}
          events={events}
          handleTradeClick={onTradeClick}
          isResolvedFilter={isResolvedFilter}
        />
      </Box>

      <LoadMoreButton
        onClick={onFetchNextPage}
        isLoading={pagination.isFetchingNextPage}
        hasNextPage={pagination.hasNextPage}
        events={events}
      />
    </>
  );
};
